/**
 * Attaching a receipt line to orders: which lines can be, the orders offered, and the shares.
 *
 * The server splits a line equally across the orders chosen, less any amounts the user set
 * (`orders.services.split`). `shares` mirrors it so each order's part shows before saving; the
 * server's answer is still what is kept.
 */

import type { components } from "@/api/schema";
import type { OrderStatus } from "@/features/orders/lifecycle";
import { parseMoney } from "@/lib/money";
import type { AllocationsBody } from "./queries";
import type { EditLine } from "./review";

type OrderSummary = components["schemas"]["OrderSummaryOut"];

/** A business's line with something to share, and saved, so the server knows its id. */
export function canAttach(line: EditLine): boolean {
  return (
    line.bucket_type === "business" &&
    line.business_id !== null &&
    line.line_total.minor > 0 &&
    !line.isNew &&
    (line.merged?.length ?? 0) === 0
  );
}

const OPEN: readonly OrderStatus[] = ["draft", "confirmed", "in_progress"];

/** Orders still being worked on first, since that is usually what is being bought for; then the
 * rest, newest first within each. A finished order can still have had costs. */
export function orderChoices(orders: OrderSummary[]): OrderSummary[] {
  const open = orders.filter((order) => OPEN.includes(order.status));
  const done = orders.filter((order) => !OPEN.includes(order.status));
  return [...open, ...done];
}

/** One chosen order, with the amount typed for it: empty to take an equal part of the rest. */
export interface Pick {
  orderId: string;
  amount: string;
}

/** Each pick's share in minor units, as the server would split it, or null when an amount does
 * not parse or the amounts set come to more than the line. */
export function shares(totalMinor: number, picks: Pick[], currency: string): number[] | null {
  const set: (number | null)[] = [];
  for (const pick of picks) {
    if (pick.amount.trim() === "") {
      set.push(null);
      continue;
    }
    const parsed = parseMoney(pick.amount, currency);
    if (!parsed || parsed.minor < 0) return null;
    set.push(parsed.minor);
  }
  const rest = totalMinor - set.reduce<number>((sum, amount) => sum + (amount ?? 0), 0);
  if (rest < 0) return null;
  const equal = set.flatMap((amount, index) => (amount === null ? [index] : []));
  const base = equal.length ? Math.floor(rest / equal.length) : 0;
  const extra = equal.length ? rest % equal.length : 0;
  return set.map((amount, index) => {
    if (amount !== null) return amount;
    return base + (equal.indexOf(index) < extra ? 1 : 0);
  });
}

/** The PUT body: amounts only for the orders the user set one for. */
export function toAllocationsBody(picks: Pick[], currency: string): AllocationsBody {
  return {
    orders: picks.map((pick) => {
      const parsed = pick.amount.trim() === "" ? null : parseMoney(pick.amount, currency);
      return parsed
        ? { order_id: pick.orderId, amount_minor: parsed.minor }
        : { order_id: pick.orderId };
    }),
  };
}
